// The three-arm allocation diagram: one shared checkpoint branching into the
// arms, each described directly underneath its own box, with the quantities
// held equal across all three stated once below the whole graphic.

import { count } from "../data/format";
import type { ExperimentSummary } from "../data/schema";

const ARMS = [
  {
    id: "bc_base",
    cls: "node--base",
    label: "Original agent",
    detail: "Kept without further training",
  },
  {
    id: "extra_demo",
    cls: "node--extra",
    label: "Extra demonstrations",
    detail: "New examples along routes chosen by the expert",
  },
  {
    id: "recovery",
    cls: "node--recovery",
    label: "Recovery labels",
    detail:
      "Advice for states the learner actually reaches after a changed action",
  },
] as const;

export function renderThreeArm(
  mount: HTMLElement,
  summary: ExperimentSummary,
): void {
  const budget = count(summary.budget.additional_revealed_targets.recovery);
  const wrapper = document.createElement("div");
  wrapper.className = "arm-diagram";
  wrapper.innerHTML = `
    <svg viewBox="0 0 960 270" role="img">
      <title>Three agents with the same starting point</title>
      <desc>The original agent is kept as a reference. Two copies receive equal
        numbers of new labels, collected in different situations.</desc>
      <rect class="node" x="378" y="16" width="204" height="56" rx="8"></rect>
      <text x="480" y="40" text-anchor="middle">one trained agent</text>
      <text x="480" y="60" text-anchor="middle" class="detail">two copies for further training</text>
      ${ARMS.map((arm, index) => {
        const x = 60 + index * 300;
        return `
          <path class="edge" d="M 480 72 C 480 108, ${x + 120} 118, ${x + 120} 150"></path>
          <g class="arm">
            <rect class="node ${arm.cls}" x="${x}" y="150" width="240"
                  height="70" rx="8" stroke-width="2"></rect>
            <text x="${x + 120}" y="178" text-anchor="middle">${arm.label}</text>
            <text x="${x + 120}" y="200" text-anchor="middle" class="detail">
              ${arm.id === "bc_base" ? "+0 labels" : `+${budget} labels`}</text>
          </g>`;
      }).join("")}
    </svg>
    <div class="arm-legend">
      ${ARMS.map(
        (arm) => `
        <p data-arm="${arm.id}">
          <b>${arm.label} · ${arm.id === "bc_base" ? "+0" : `+${budget}`} labels.</b> ${arm.detail}.
        </p>`,
      ).join("")}
    </div>
    <p class="arm-shared small">
      <b>Held equal:</b> the two trained copies get the same label budget,
      training updates, and uses of each type of label. All three agents face the same test scenarios.
    </p>
  `;

  mount.replaceChildren(wrapper);
}
